import jsPDF from 'jspdf';
import { Employee, Department } from '../data/employees';

interface ReportData {
  stats: {
    totalEmployees: number;
    highRiskCount: number;
    mediumRiskCount: number;
    lowRiskCount: number;
    percentDisengaged: number;
    avgRiskScore: number;
    estimatedCost: number;
  };
  departments: Department[];
  highRiskEmployees: Employee[];
  generatedDate: string;
}

export const generateEngagementReport = async (data: ReportData): Promise<void> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 20;

  // Colors
  const grape = [105, 39, 127];
  const blackberry = [67, 22, 82];
  const javaBanana = [6, 188, 202];
  const charcoal = [31, 41, 55];
  const gray = [107, 114, 128];

  // Helper function to add new page if needed
  const checkPageBreak = (requiredSpace: number) => {
    if (yPosition + requiredSpace > pageHeight - 20) {
      doc.addPage();
      yPosition = 20;
    }
  };

  // Header
  doc.setFillColor(blackberry[0], blackberry[1], blackberry[2]);
  doc.rect(0, 0, pageWidth, 45, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('dynami', 20, 28);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Engagement Risk Report', pageWidth - 20, 25, { align: 'right' });
  doc.setFontSize(10);
  doc.text(`Generated: ${data.generatedDate}`, pageWidth - 20, 35, { align: 'right' });

  yPosition = 60;

  // Executive Summary
  doc.setTextColor(grape[0], grape[1], grape[2]);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Executive Summary', 20, yPosition);
  yPosition += 15;

  // Stats boxes
  const boxWidth = (pageWidth - 50) / 4;
  const boxHeight = 35;
  const stats = [
    { label: 'Total Employees', value: data.stats.totalEmployees.toString() },
    { label: 'Disengaged', value: `${data.stats.percentDisengaged}%` },
    { label: 'High Risk', value: data.stats.highRiskCount.toString() },
    { label: 'Est. Cost', value: `$${(data.stats.estimatedCost / 1000).toFixed(0)}K` },
  ];

  stats.forEach((stat, index) => {
    const x = 20 + index * (boxWidth + 5);
    doc.setFillColor(248, 249, 252);
    doc.roundedRect(x, yPosition, boxWidth, boxHeight, 3, 3, 'F');
    
    doc.setTextColor(charcoal[0], charcoal[1], charcoal[2]);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(stat.value, x + boxWidth / 2, yPosition + 15, { align: 'center' });
    
    doc.setTextColor(gray[0], gray[1], gray[2]);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(stat.label, x + boxWidth / 2, yPosition + 27, { align: 'center' });
  });

  yPosition += boxHeight + 25;

  // Risk Distribution
  checkPageBreak(80);
  doc.setTextColor(grape[0], grape[1], grape[2]);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Risk Distribution', 20, yPosition);
  yPosition += 15;

  // Risk bars
  const riskData = [
    { label: 'High Risk', count: data.stats.highRiskCount, color: [239, 68, 68] },
    { label: 'Medium Risk', count: data.stats.mediumRiskCount, color: [245, 158, 11] },
    { label: 'Low Risk', count: data.stats.lowRiskCount, color: [16, 185, 129] },
  ];

  const maxCount = Math.max(...riskData.map(r => r.count));
  const barMaxWidth = pageWidth - 100;

  riskData.forEach((risk) => {
    doc.setTextColor(charcoal[0], charcoal[1], charcoal[2]);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(risk.label, 20, yPosition + 5);
    
    const barWidth = (risk.count / maxCount) * barMaxWidth;
    doc.setFillColor(risk.color[0], risk.color[1], risk.color[2]);
    doc.roundedRect(70, yPosition - 3, barWidth, 10, 2, 2, 'F');
    
    doc.setTextColor(charcoal[0], charcoal[1], charcoal[2]);
    doc.text(risk.count.toString(), 75 + barWidth, yPosition + 5);
    
    yPosition += 18;
  });

  yPosition += 15;

  // Department Breakdown
  checkPageBreak(80);
  doc.setTextColor(grape[0], grape[1], grape[2]);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Department Breakdown', 20, yPosition);
  yPosition += 15;

  // Department table header
  doc.setFillColor(248, 249, 252);
  doc.rect(20, yPosition, pageWidth - 40, 10, 'F');
  doc.setTextColor(gray[0], gray[1], gray[2]);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Department', 25, yPosition + 7);
  doc.text('Employees', 90, yPosition + 7);
  doc.text('Avg DDI', 120, yPosition + 7);
  doc.text('High', 150, yPosition + 7);
  doc.text('Medium', 170, yPosition + 7);
  doc.text('Low', 195, yPosition + 7);
  yPosition += 15;

  // Department rows
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(charcoal[0], charcoal[1], charcoal[2]);
  
  data.departments.forEach((dept, index) => {
    checkPageBreak(12);
    
    if (index % 2 === 0) {
      doc.setFillColor(252, 252, 253);
      doc.rect(20, yPosition - 5, pageWidth - 40, 12, 'F');
    }
    
    doc.setFontSize(9);
    doc.text(dept.name, 25, yPosition + 2);
    doc.text(dept.headCount.toString(), 95, yPosition + 2);
    doc.text(dept.avgRiskScore.toString(), 125, yPosition + 2);
    
    doc.setTextColor(239, 68, 68);
    doc.text(dept.highRiskCount.toString(), 155, yPosition + 2);
    doc.setTextColor(245, 158, 11);
    doc.text(dept.mediumRiskCount.toString(), 175, yPosition + 2);
    doc.setTextColor(16, 185, 129);
    doc.text(dept.lowRiskCount.toString(), 198, yPosition + 2);
    doc.setTextColor(charcoal[0], charcoal[1], charcoal[2]);
    
    yPosition += 12;
  });

  yPosition += 20;

  // High Risk Employees (new page)
  doc.addPage();
  yPosition = 20;

  doc.setTextColor(grape[0], grape[1], grape[2]);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('High Risk Employees Requiring Attention', 20, yPosition);
  yPosition += 15;

  // Employee cards
  data.highRiskEmployees.slice(0, 10).forEach((emp, index) => {
    checkPageBreak(45);

    // Card background
    doc.setFillColor(254, 242, 242);
    doc.roundedRect(20, yPosition, pageWidth - 40, 40, 3, 3, 'F');

    // Employee info
    doc.setTextColor(charcoal[0], charcoal[1], charcoal[2]);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(emp.name, 25, yPosition + 12);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(gray[0], gray[1], gray[2]);
    doc.text(`${emp.role} • ${emp.department}`, 25, yPosition + 22);

    // Risk score
    doc.setFillColor(239, 68, 68);
    doc.roundedRect(pageWidth - 55, yPosition + 8, 30, 20, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(emp.riskScore.toString(), pageWidth - 40, yPosition + 21, { align: 'center' });

    // Behavioral flags
    if (emp.behavioralFlags.length > 0) {
      doc.setTextColor(charcoal[0], charcoal[1], charcoal[2]);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      const flagText = `Flags: ${emp.behavioralFlags.slice(0, 2).join(', ')}${emp.behavioralFlags.length > 2 ? '...' : ''}`;
      doc.text(flagText, 25, yPosition + 33);
    }

    yPosition += 48;
  });

  // Footer on last page
  const totalPages = doc.internal.pages.length - 1;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(gray[0], gray[1], gray[2]);
    doc.text(
      `Page ${i} of ${totalPages} • Confidential - Generated by Dynami`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  }

  // Save the PDF
  doc.save('dynami-engagement-report.pdf');
};

export const exportEmployeeProfile = async (employee: Employee): Promise<void> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Colors
  const grape = [105, 39, 127];
  const blackberry = [67, 22, 82];
  const charcoal = [31, 41, 55];
  const gray = [107, 114, 128];

  // Header
  doc.setFillColor(blackberry[0], blackberry[1], blackberry[2]);
  doc.rect(0, 0, pageWidth, 40, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('dynami', 20, 25);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Employee Profile Report', pageWidth - 20, 25, { align: 'right' });

  let yPosition = 55;

  // Employee name and role
  doc.setTextColor(grape[0], grape[1], grape[2]);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(employee.name, 20, yPosition);
  yPosition += 10;

  doc.setTextColor(gray[0], gray[1], gray[2]);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`${employee.role} • ${employee.department}`, 20, yPosition);
  yPosition += 25;

  // Risk Score Box
  const riskColor = employee.riskLevel === 'High' ? [239, 68, 68] : 
                    employee.riskLevel === 'Medium' ? [245, 158, 11] : [16, 185, 129];
  
  doc.setFillColor(riskColor[0], riskColor[1], riskColor[2]);
  doc.roundedRect(20, yPosition, 60, 40, 5, 5, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text(employee.riskScore.toString(), 50, yPosition + 22, { align: 'center' });
  doc.setFontSize(10);
  doc.text('DDI', 50, yPosition + 34, { align: 'center' });

  // Info box
  doc.setFillColor(248, 249, 252);
  doc.roundedRect(90, yPosition, pageWidth - 110, 40, 5, 5, 'F');
  
  doc.setTextColor(charcoal[0], charcoal[1], charcoal[2]);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Email: ${employee.email}`, 100, yPosition + 12);
  doc.text(`Manager: ${employee.manager}`, 100, yPosition + 24);
  doc.text(`Start Date: ${employee.startDate}`, 100, yPosition + 36);

  yPosition += 55;

  // Risk Explanation
  doc.setTextColor(grape[0], grape[1], grape[2]);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Risk Analysis', 20, yPosition);
  yPosition += 10;

  doc.setTextColor(charcoal[0], charcoal[1], charcoal[2]);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const splitText = doc.splitTextToSize(employee.riskExplanation, pageWidth - 40);
  doc.text(splitText, 20, yPosition);
  yPosition += splitText.length * 5 + 15;

  // Behavioral Flags
  if (employee.behavioralFlags.length > 0) {
    doc.setTextColor(grape[0], grape[1], grape[2]);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Behavioral Flags', 20, yPosition);
    yPosition += 10;

    doc.setTextColor(charcoal[0], charcoal[1], charcoal[2]);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    employee.behavioralFlags.forEach((flag) => {
      doc.text(`• ${flag}`, 25, yPosition);
      yPosition += 7;
    });
  }

  // Save
  doc.save(`dynami-profile-${employee.name.replace(/\s+/g, '-').toLowerCase()}.pdf`);
};


